<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Transport"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasTransportData"
            :has-errors="hasSectionErrors"
          />
          Transport
        </span>
      </template>
      <validation-observer>
        <!-- Transport Section -->
        <b-row v-if="parcel.transport_data">
          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Transport mode at border"
              rules=""
            >
              <b-form-group>
                <label>
                  Transport mode at border <small class="text-muted">1903000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Transport mode at border'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="parcel.transport_data.border_transport_means_mode"
                  :classifier-key="classifierKeys.transportModes"
                  :country="classifierCountry"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Transport means registration nationality at border"
            >
              <b-form-group>
                <label>
                  Transport means registration nationality at border
                  <small class="text-muted">1908062000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Transport means registration nationality at border'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="parcel.transport_data.border_transport_means_registration_nationality"
                  :classifier-key="classifierKeys.countries"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Transport mode inland"
              rules=""
            >
              <b-form-group>
                <label>
                  Transport mode inland <small class="text-muted">1904000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Transport mode inland'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="parcel.transport_data.arrival_transport_means_mode"
                  :classifier-key="classifierKeys.transportModes"
                  :country="classifierCountry"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Transport arrival means identification type"
            >
              <b-form-group>
                <label>
                  Transport arrival means identification type
                  <small class="text-muted">1906061000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Transport arrival means identification type'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="parcel.transport_data.arrival_transport_means_identification_type"
                  :classifier-key="classifierKeys.transportIdentificationTypes"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Transport arrival means identification"
            >
              <b-form-group>
                <label>
                  Transport arrival means identification
                  <small class="text-muted">1906017000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Transport arrival means identification'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="parcel.transport_data.arrival_transport_means_identification"
                  v-b-tooltip.hover.top="getIssueMessage('transport_data.arrival_transport_means_identification')"
                  :class="getIssueClass('transport_data.arrival_transport_means_identification')"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Country of dispatch"
            >
              <b-form-group>
                <label>
                  Country of dispatch
                  <small class="text-muted">1606000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Country from which goods are dispatched'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="parcel.transport_data.export_country"
                  :classifier-key="classifierKeys.countries"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Country of destination"
            >
              <b-form-group>
                <label>
                  Country of destination
                  <small class="text-muted">1603000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Country of destination for the goods'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="parcel.transport_data.destination_country"
                  :classifier-key="classifierKeys.countries"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <b-form-group>
              <label>
                Region of destination
                <small class="text-muted">1604000000</small>
              </label>
              <b-form-input
                v-model="parcel.transport_data.destination_region"
                v-b-tooltip.hover.top="getIssueMessage('transport_data.destination_region')"
                :class="getIssueClass('transport_data.destination_region')"
                :disabled="disabled"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import { CUSTOMS_CLASSIFIER_KEYS, classifierCountryParam } from '@/libs/customsClassifiers'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
    ClassifierSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    entryCountry: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      classifierKeys: CUSTOMS_CLASSIFIER_KEYS,
    }
  },
  computed: {
    // Transport-mode validation is country-aware on the backend (FI uses the
    // Tulli CL018 variant), so the selects ask for the country snapshot.
    classifierCountry() {
      return classifierCountryParam(this.entryCountry)
    },
    hasTransportData() {
      if (!this.parcel || !this.parcel.transport_data) {
        return false
      }
      const {
        border_transport_means_mode: borderTransportMeansMode,
        border_transport_means_registration_nationality: borderTransportMeansRegistrationNationality,
        arrival_transport_means_mode: arrivalTransportMeansMode,
        arrival_transport_means_identification_type: arrivalTransportMeansIdentificationType,
        arrival_transport_means_identification: arrivalTransportMeansIdentification,
        export_country: exportCountry,
        destination_country: destinationCountry,
        destination_region: destinationRegion,
      } = this.parcel.transport_data
      return !!(
        borderTransportMeansMode
        || borderTransportMeansRegistrationNationality
        || arrivalTransportMeansMode
        || arrivalTransportMeansIdentificationType
        || arrivalTransportMeansIdentification
        || exportCountry
        || destinationCountry
        || destinationRegion
      )
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('transport_data.'))
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
